/**
 * 手机竖屏外壳（T01）。
 *
 * - 逻辑画布固定 390 x 844，与 `src/ui/measure.ts` 的 FRAME_WIDTH / FRAME_HEIGHT 同源；
 * - 窗口小于画布时整体 `transform: scale()` 等比缩小，**内部布局坐标不变**；
 * - 顶部状态栏安全区 24px、底部 home 指示条安全区 20px；
 * - 桌面端画布外显示深色底衬；
 * - 画布 / 两个安全区 / home 指示条都登记进组件注册表，供安卓移植清单使用。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MutableRefObject, ReactElement, ReactNode } from 'react';

import {
  FRAME_HEIGHT,
  FRAME_WIDTH,
  attachElement,
  buildComponentMeta,
  getRegisteredComponent,
  metaSignature,
  registerComponent,
} from '../ui';
import type { ComponentMetaInput } from '../ui';
import { FrameBox } from './FrameBox';
import { readViewportScale } from './canvasScale';
import styles from './PhoneFrame.module.css';

/** 顶部状态栏安全区高度（逻辑 dp）。 */
export const STATUS_BAR_HEIGHT = 24;
/** 底部 home 指示条安全区高度（逻辑 dp）。 */
export const HOME_INDICATOR_HEIGHT = 20;
/** home 指示条的逻辑盒（居中于底部安全区）。 */
export const HOME_INDICATOR_BOX = { x: 128, y: 832, width: 134, height: 4 } as const;

const FRAME_META: ComponentMetaInput = {
  id: 'app.frame',
  page: 'global',
  role: 'container',
  label: '手机竖屏画布',
  layout: { x: 0, y: 0, width: FRAME_WIDTH, height: FRAME_HEIGHT },
  zIndex: 0,
  notes: '根容器，安卓用全屏 View；逻辑画布固定 390x844，缩放只作用于视觉',
};

const SAFE_AREA_TOP_META: ComponentMetaInput = {
  id: 'app.safeArea.top',
  page: 'global',
  role: 'container',
  label: '顶部状态栏安全区',
  layout: { x: 0, y: 0, width: FRAME_WIDTH, height: STATUS_BAR_HEIGHT },
  zIndex: 1,
  notes: '安卓用 WindowInsets.systemBars 顶部内边距；此区域不放置可点部件',
};

const SAFE_AREA_BOTTOM_META: ComponentMetaInput = {
  id: 'app.safeArea.bottom',
  page: 'global',
  role: 'container',
  label: '底部 home 指示条安全区',
  layout: {
    x: 0,
    y: FRAME_HEIGHT - HOME_INDICATOR_HEIGHT,
    width: FRAME_WIDTH,
    height: HOME_INDICATOR_HEIGHT,
  },
  zIndex: 1,
  notes: '安卓用 WindowInsets 底部内边距 / gesture bar',
};

const HOME_INDICATOR_META: ComponentMetaInput = {
  id: 'app.homeIndicator',
  page: 'global',
  role: 'icon',
  label: 'home 指示条',
  layout: { ...HOME_INDICATOR_BOX },
  zIndex: 2,
  notes: '纯装饰条，安卓用手势导航 handle 或直接隐藏',
};

export interface PhoneFrameProps {
  children?: ReactNode;
  /** 画布节点；传入后 T04 的清单采集在开发态可按画布 scale 校准坐标。 */
  frameRef?: MutableRefObject<HTMLDivElement | null>;
  /** 强制缩放系数（预览 / 测试用）；不传则按窗口尺寸自动计算。 */
  scale?: number;
}

export function PhoneFrame({ children, frameRef, scale }: PhoneFrameProps): ReactElement {
  const [autoScale, setAutoScale] = useState(readViewportScale);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const frameMeta = useMemo(() => buildComponentMeta(FRAME_META, 'container'), []);

  useEffect(() => {
    const update = (): void => {
      setAutoScale(readViewportScale());
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  useEffect(() => {
    const existing = getRegisteredComponent(frameMeta.id);
    // StrictMode and route reloads reuse the same canvas definition.
    if (!existing || metaSignature(existing.meta) !== metaSignature(frameMeta)) registerComponent(frameMeta);
    attachElement(frameMeta.id, canvasRef.current);
    return () => {
      attachElement(frameMeta.id, null);
    };
  }, [frameMeta]);

  const setCanvasNode = useCallback(
    (node: HTMLDivElement | null): void => {
      canvasRef.current = node;
      if (frameRef !== undefined) frameRef.current = node;
    },
    [frameRef],
  );

  const effectiveScale = scale ?? autoScale;

  return (
    <div className={styles.viewport}>
      <div
        ref={setCanvasNode}
        className={styles.canvas}
        data-testid="phone-canvas"
        data-ui-id={frameMeta.id}
        data-ui-role={frameMeta.role}
        style={{
          width: FRAME_WIDTH,
          height: FRAME_HEIGHT,
          transform: `scale(${effectiveScale})`,
        }}
      >
        <FrameBox meta={SAFE_AREA_TOP_META} className={styles.safeArea} />
        <FrameBox meta={SAFE_AREA_BOTTOM_META} className={styles.safeArea} />
        <FrameBox meta={HOME_INDICATOR_META} className={styles.homeIndicator} />
        {children}
      </div>
    </div>
  );
}
